import { useQuery } from 'react-query';
import axios, { AxiosError, AxiosResponse } from 'axios';

import { ApiController } from 'core/api/client-portal/autogenerated/Api';
import { Interceptors } from 'core/api/client-portal/autogenerated/http-client';
import { refreshToken } from 'core/api/spectre-control/hooks/spectre-control-refresh/use-refresh-token';
import { useViewController } from './use-view-controller';
import useDepositNowStore, {
    getVaultToken,
    setVaultToken
} from 'core/stores/deposit-now-store';

export type SpectreTokenResponse = {
    id: string;
    location: string;
    spectreControlUrl: string;
    spectreAgentUrl: string;
    authToken: {
        id: string;
        location: string;
        token: string;
        audiences: [string, string];
    };
};

export function getSpectreAuthController(token = getVaultToken()) {
    const interceptors: Interceptors = {
        request: [
            {
                onFulfilled: (config) => {
                    if (!config.headers) config.headers = {};

                    config.headers['Cache-Control'] =
                        'no-cache, no-store, must-revalidate';
                    config.headers['Pragma'] = 'no-cache';
                    config.headers['Expires'] = '0';

                    return config;
                }
            }
        ],
        response: [
            {
                onFulfilled: (response: AxiosResponse<unknown>) => {
                    // Any status code within the range of 2xx triggers this function
                    // Do something with response data
                    return response;
                },
                onRejected: async (error: AxiosError) => {
                    // Any status codes outside the range of 2xx trigger this function
                    // Do something with response error
                    // await gettoken
                    console.log('Error in response interceptor', error);
                    if (error.response?.status === 401) {
                        const newToken = await refreshToken();
                        setVaultToken(newToken as string);
                    }
                    if (error.response?.status === 415) {
                        const newToken = await refreshToken();
                        setVaultToken(newToken as string);
                    }
                    console.log('onRejected URL', error.config?.url);

                    const config =
                        (error.config as Record<string, unknown>) ?? {};
                    config.headers = {
                        ...error.config?.headers,
                        authorization: `Bearer ${getVaultToken()}`,
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        Expires: '0',
                        Pragma: 'no-cache',
                        // 'Content-Type': 'multipart/form-data',
                        withCredentials: true
                    };
                    console.log('config', config);
                    const retryResponses = [401, 502];

                    if (
                        !config._retry &&
                        retryResponses.includes(error.response?.status ?? 0)
                    ) {
                        // Mark as retried to avoid infinite loops
                        config._retry = true;

                        // Wait 2 seconds before retrying
                        return new Promise((resolve) => {
                            setTimeout(() => {
                                console.log(
                                    'Retrying request - config >',
                                    config
                                );
                                resolve(axios(config));
                            }, 2000);
                        });
                    }

                    return Promise.reject(error);
                }
            }
        ]
    };
    const controller = new ApiController({
        interceptors,
        securityWorker: () => ({
            headers: {
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                Expires: '0',
                Pragma: 'no-cache'
                // 'Content-Type': 'multipart/form-data'
            },
            withCredentials: true
        })
    });
    return controller;
}

export function useNewRequestSpectreToken(depositId: number) {
    const viewController = useViewController();
    const {
        setDepositUniqueReference,
        setShouldClaimToken,
        setSpectreAgentUrl,
        setSpectreControlUrl,
        setSpectreRequestToken,
        setVaultToken
    } = useDepositNowStore();

    return useQuery<string, ReactQueryError<Error>>(
        ['use-request-token'],
        async () => {
            const response = await viewController.request<SpectreTokenResponse>(
                {
                    path: `/api/v1/deposits/${depositId}/spectretoken`,
                    method: 'GET',
                    secure: true,
                    format: 'json'
                }
            );

            const spectreControlUrl = response?.data?.spectreControlUrl;
            const spectreAgentUrl = response?.data?.spectreAgentUrl;
            const depositUniqueReference = response?.data?.id;

            // set both urls in the store for easy access
            if (spectreControlUrl && spectreAgentUrl) {
                setSpectreAgentUrl(spectreAgentUrl);
                setSpectreControlUrl(spectreControlUrl);
                setDepositUniqueReference(depositUniqueReference);
            }

            const { data } = response;
            setSpectreRequestToken(data.authToken.token);

            const spectreAuthController = getSpectreAuthController(
                data.authToken.token
            );

            const authUrl = new URL(
                `/api/auth/${data.authToken.id}`,
                data.spectreControlUrl
            );

            const spectreResponse = await spectreAuthController.request<{
                token: string;
            }>({
                baseURL: authUrl.origin,
                path: `/api/auth/${data.authToken.id}`,
                method: 'GET',
                secure: true,
                format: 'json',
                query: {
                    depositId: data.id
                },
                withCredentials: true
            });

            setVaultToken(spectreResponse.data.token);
            return spectreResponse.data.token;
        },
        {
            onSettled: (data) => {
                setShouldClaimToken(true);
            }
        }
    );
}

function useToken(depositId: number) {
    const { data: jwt, isLoading: isTokenLoading } =
        useNewRequestSpectreToken(depositId);

    return { jwt, isTokenLoading };
}

export default useToken;
